import * as React from 'react'
import {
  useIsLoggedInQuery as useIsLoggedInQueryBase,
  useUserQuery as useUserQueryBase,
} from 'auth/hooks'
import { useUserStudioAiSubscriptionAccessQuery as useUserStudioAiSubscriptionAccessQueryBase } from '@repo/cf-user/hooks/use-user-data'
import { getPathnameWithoutLocale } from 'cf-i18n/helpers'
import { useFeatureIsOn as useFeatureIsOnBase } from 'growthbook'
import { usePaymentsDialog as usePaymentsDialogBase } from 'payments/components/payments-dialog-wrapper-context'

import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint as savePaymentModalEntryPointBase,
} from 'payments/payments-dialog/analytics'
import { sessionStore as sessionStoreBase } from 'utils/storage'

export const PAYMENT_NUDGE_STORAGE_KEY = 'cfPaymentNudgeShown'
export const FEATURE_HOMEPAGE_CREATE_TAB_STUDIO_AI_TRIAL =
  'GROW-2944-homepage-create-tab-studio-ai-trial'
type PaymentNudgeDefaultPlan = 'aiStudio'

export interface PaymentNudgeDependencies {
  getPathname: () => string
  savePaymentModalEntryPoint: typeof savePaymentModalEntryPointBase
  sessionStore: Pick<typeof sessionStoreBase, 'getString' | 'setString'>
  useFeatureIsOn: typeof useFeatureIsOnBase
  useIsLoggedInQuery: typeof useIsLoggedInQueryBase
  usePaymentsDialog: typeof usePaymentsDialogBase
  useUserQuery: typeof useUserQueryBase
  useUserStudioAiSubscriptionAccessQuery: typeof useUserStudioAiSubscriptionAccessQueryBase
}

const defaultPaymentNudgeDependencies: PaymentNudgeDependencies = {
  getPathname: () => (typeof window !== 'undefined' ? window.location.pathname : ''),
  savePaymentModalEntryPoint: savePaymentModalEntryPointBase,
  sessionStore: sessionStoreBase,
  useFeatureIsOn: useFeatureIsOnBase,
  useIsLoggedInQuery: useIsLoggedInQueryBase,
  usePaymentsDialog: usePaymentsDialogBase,
  useUserQuery: useUserQueryBase,
  useUserStudioAiSubscriptionAccessQuery: useUserStudioAiSubscriptionAccessQueryBase,
}

function isHomepageCreateTab(pathname: string) {
  return getPathnameWithoutLocale(pathname) === '/'
}

function hasActiveStudioAiTrialOrSubscription(
  studioAiSubscriptionAccess: ReturnType<
    PaymentNudgeDependencies['useUserStudioAiSubscriptionAccessQuery']
  >['data'],
) {
  return (
    studioAiSubscriptionAccess?.isSubscribed ||
    studioAiSubscriptionAccess?.subscription?.isTrial === true
  )
}

function Nudge({
  defaultPlan,
  dependencies,
}: {
  defaultPlan?: PaymentNudgeDefaultPlan
  dependencies: PaymentNudgeDependencies
}) {
  const { savePaymentModalEntryPoint, sessionStore, usePaymentsDialog } = dependencies
  const isNavigatingRef = React.useRef(false)
  const setNavigating = (value: boolean) => {
    isNavigatingRef.current = value // Use a ref instead of state to avoid stale closure issue
  }

  const { open: openPayments } = usePaymentsDialog()
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (!sessionStore.getString(PAYMENT_NUDGE_STORAGE_KEY) && !isNavigatingRef.current) {
        sessionStore.setString(PAYMENT_NUDGE_STORAGE_KEY, 'true')

        if (document.querySelector('[data-payments-dialog]')) {
          return
        }

        savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.AUTO_TRIGGERED)
        if (window.cfOpenPaymentsDialog) {
          if (defaultPlan) {
            window.cfOpenPaymentsDialog({ defaultPlan, useCase: 'nudge' })
          } else {
            window.cfOpenPaymentsDialog({ useCase: 'nudge' })
          }
        } else {
          openPayments(defaultPlan, 'nudge')
        }
      }
    }, 5000)

    return () => clearTimeout(timeout)
  }, [defaultPlan, openPayments, savePaymentModalEntryPoint, sessionStore])

  React.useEffect(() => {
    const handleNavigation = () => {
      setNavigating(true)
    }
    window.addEventListener('popstate', handleNavigation)
    window.addEventListener('beforeunload', handleNavigation)

    return () => {
      window.removeEventListener('popstate', handleNavigation)
      window.removeEventListener('beforeunload', handleNavigation)
    }
  }, [])

  return null
}

function HomepageCreateTabPaymentNudge({
  canShowDefaultNudge,
  dependencies,
}: {
  canShowDefaultNudge: boolean
  dependencies: PaymentNudgeDependencies
}) {
  const { useUserStudioAiSubscriptionAccessQuery } = dependencies
  const { data: studioAiSubscriptionAccess, isPending } = useUserStudioAiSubscriptionAccessQuery()

  if (isPending) {
    return null
  }

  if (
    studioAiSubscriptionAccess &&
    !hasActiveStudioAiTrialOrSubscription(studioAiSubscriptionAccess)
  ) {
    return (
      <HomepageCreateTabPaymentNudgeExperiment
        canShowDefaultNudge={canShowDefaultNudge}
        dependencies={dependencies}
      />
    )
  }

  return canShowDefaultNudge ? <Nudge dependencies={dependencies} /> : null
}

function HomepageCreateTabPaymentNudgeExperiment({
  canShowDefaultNudge,
  dependencies,
}: {
  canShowDefaultNudge: boolean
  dependencies: PaymentNudgeDependencies
}) {
  const { useFeatureIsOn } = dependencies
  const shouldPreloadStudioAi = useFeatureIsOn(FEATURE_HOMEPAGE_CREATE_TAB_STUDIO_AI_TRIAL)

  if (shouldPreloadStudioAi) {
    return <Nudge defaultPlan="aiStudio" dependencies={dependencies} />
  }

  return canShowDefaultNudge ? <Nudge dependencies={dependencies} /> : null
}

export function PaymentNudge({
  dependencies = defaultPaymentNudgeDependencies,
}: {
  dependencies?: PaymentNudgeDependencies
} = {}) {
  const { getPathname, useIsLoggedInQuery, useUserQuery } = dependencies
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { data: hasAllAccess, isPending } = useUserQuery(undefined, {
    select(data) {
      return data.me?.user?.subscriptionAccess.allAccess.isSubscribed ?? false
    },
  })

  if (!isLoggedIn || isPending) {
    return null
  }

  if (isHomepageCreateTab(getPathname())) {
    return (
      <HomepageCreateTabPaymentNudge
        canShowDefaultNudge={!hasAllAccess}
        dependencies={dependencies}
      />
    )
  }

  if (!hasAllAccess) {
    return <Nudge dependencies={dependencies} />
  }

  return null
}
